import { CloseCircleFilled, DownloadOutlined, FileImageOutlined, FilePdfOutlined, FileOutlined, PlusOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, Divider, Empty, message, Modal, Select, Table, Tag, Upload } from 'antd';
import { format } from 'date-fns';
import { useState } from 'react';
import { apiAccountDocumentUpload } from '../../../../../api/accounts';
import './Documents.scss';

const STATUS_COLORS = {
  pending: 'orange',
  approved: 'green',
  rejected: 'red',
};

const STATUS_LABELS = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  UNDEFINED: 'Indefinido',
};

const CATEGORY_LABELS = {
  identificacao: 'Identificação',
  residencia: 'Residência',
  atividade: 'Atividade',
  SELFIE: 'Selfie',
  CNH_FULL: 'CNH Completa',
  CNH_FRONT: 'CNH Frente',
  CNH_BACK: 'CNH Verso',
  RG_FRONT: 'RG Frente',
  RG_BACK: 'RG Verso',
};

const DOCUMENT_TYPES = [
  { value: 'SELFIE', label: 'Selfie' },
  { value: 'CNH_FULL', label: 'CNH Completa' },
  { value: 'CNH_FRONT', label: 'CNH Frente' },
  { value: 'CNH_BACK', label: 'CNH Verso' },
  { value: 'RG_FRONT', label: 'RG Frente' },
  { value: 'RG_BACK', label: 'RG Verso' },
];

const ACCEPTED_EXTENSIONS = '.png,.jpg,.jpeg,.webp,.bmp,.heic,.heif';

const getFileIcon = (mimeType, extension) => {
  if (mimeType === 'application/pdf' || extension === 'pdf') {
    return <FilePdfOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />;
  }
  const imageExtensions = ['png', 'jpg', 'jpeg', 'webp', 'bmp', 'heic', 'heif'];
  if (mimeType?.startsWith('image/') || imageExtensions.includes(extension)) {
    return <FileImageOutlined style={{ fontSize: 20, color: '#1890ff' }} />;
  }
  return <FileOutlined style={{ fontSize: 20, color: '#8c8c8c' }} />;
};

const columns = [
  {
    title: '',
    dataIndex: 'mime_type',
    key: 'icon',
    width: 50,
    render: (_, record) => getFileIcon(record.mime_type, record.extension),
  },
  {
    title: 'Nome',
    dataIndex: 'name',
    key: 'name',
    render: (name) => name || 'N/A',
  },
  {
    title: 'Categoria',
    dataIndex: 'category',
    key: 'category',
    render: (category) => CATEGORY_LABELS[category] || category || 'N/A',
  },
  {
    title: 'Status',
    dataIndex: 'zoop_acquirer_status',
    key: 'zoop_acquirer_status',
    render: (status) => (
      <Tag color={STATUS_COLORS[status] || 'default'}>
        {STATUS_LABELS[status] || status || 'N/A'}
      </Tag>
    ),
  },
  {
    title: 'Download',
    dataIndex: 'download_link',
    key: 'download',
    width: 100,
    render: (link) => (
      <Button
        type="link"
        icon={<DownloadOutlined />}
        disabled={!link}
        onClick={() => window.open(link, '_blank')}
      />
    ),
  },
  {
    title: 'Criado em',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (date) => date ? format(new Date(date), "dd/MM/yyyy 'às' HH:mm") : 'N/A',
  },
];

const Documents = ({ data, onUploadSuccess }) => {
  const documents = data?.documents || [];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!documentType || fileList.length === 0) {
      message.warning('Selecione o tipo de documento e o arquivo.');
      return;
    }

    setUploading(true);
    try {
      await apiAccountDocumentUpload(data?.id, fileList[0], documentType);
      message.success('Documento enviado com sucesso!');
      setIsModalOpen(false);
      setDocumentType(null);
      handleFileRemove();
      if (onUploadSuccess) onUploadSuccess();
    } catch {
      message.error('Erro ao enviar documento.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (file) => {
    setFileList([file]);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleFileRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFileList([]);
    setPreviewUrl(null);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setDocumentType(null);
    handleFileRemove();
  };

  return (
    <div className='Documents'>
      <div className='documents-header'>
        <p className='subtitle-page'>Documentos</p>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          Adicionar Documento
        </Button>
      </div>
      <Divider />
      {documents.length === 0 ? (
        <Empty description="Nenhum documento encontrado" />
      ) : (
        <Table
          dataSource={documents}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="middle"
        />
      )}

      <Modal
        title="Adicionar Documento"
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            Cancelar
          </Button>,
          <Button
            key="upload"
            type="primary"
            loading={uploading}
            disabled={!documentType || fileList.length === 0}
            onClick={handleUpload}
          >
            Enviar Documento
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Tipo de Documento</label>
          <Select
            placeholder="Selecione o tipo"
            style={{ width: '100%' }}
            value={documentType}
            onChange={setDocumentType}
            options={DOCUMENT_TYPES}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Arquivo</label>
          {fileList.length > 0 ? (
            <div className="file-preview" onClick={handleFileRemove}>
              <img src={previewUrl} alt={fileList[0].name} />
              <CloseCircleFilled className="file-preview-remove" />
              <p className="file-preview-name">{fileList[0].name}</p>
            </div>
          ) : (
            <Upload.Dragger
              accept={ACCEPTED_EXTENSIONS}
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => {
                handleFileSelect(file);
                return false;
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Clique ou arraste o arquivo aqui</p>
              <p className="ant-upload-hint">PNG, JPG, WEBP, BMP, HEIC, HEIF</p>
            </Upload.Dragger>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Documents;
